import React, { useMemo, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ModuleRegistry,
  ClientSideRowModelModule,
  CsvExportModule,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import ActionButtons from './ActionButtons';

/**
 * Generic data-grid wrapper around AG Grid.
 *
 * Before refactoring, every *Grid.js file (Customer, Item, Category,
 * Supplier, Tax, Invoice) registered the same modules, declared the
 * same defaultColDef, the same container markup, and the same action-
 * button cell renderer — about 100 lines repeated 6 times.
 *
 * Now each entity grid only needs to supply its unique `columns` array
 * and this component handles the rest.
 *
 * Props:
 *  - rowData         – array of row objects
 *  - columns         – AG Grid column definitions (entity-specific)
 *  - onEdit          – callback when Edit is clicked
 *  - onDelete        – callback when Delete is clicked
 *  - onGridReady     – exposes { exportToCsv } to parent
 *  - exportFileName  – file name for CSV export
 *  - editLabel       – label for the edit button (default 'Edit')
 *  - rowHeight       – pixel height per row (default 40)
 *  - pagination      – enable AG Grid pagination (default false)
 *  - paginationSize  – rows per page (default 20)
 */

// Register AG Grid modules once at module level
ModuleRegistry.registerModules([ClientSideRowModelModule, CsvExportModule]);

// Shared default cell renderer
const DefaultCellRenderer = (params) => {
  const isRight = params.colDef?.type === 'rightAligned';
  return (
    <div className={`flex items-center h-full ${isRight ? 'justify-end text-right w-full' : ''}`}>
      {params.valueFormatted ?? params.value}
    </div>
  );
};

export default function DataGrid({
  rowData,
  columns,
  onEdit,
  onDelete,
  onGridReady,
  exportFileName = 'export.csv',
  editLabel = 'Edit',
  rowHeight = 40,
  pagination = false,
  paginationSize = 20,
  isRowReadOnly,
  rowReadOnlyTitle = '',
}) {
  /* Expose exportToCsv to parent when grid is ready */
  const handleGridReady = useCallback(
    (params) => {
      if (onGridReady) {
        onGridReady({
          exportToCsv: () =>
            params.api.exportDataAsCsv({ fileName: exportFileName }),
        });
      }
    },
    [onGridReady, exportFileName]
  );

  /* Shared column defaults — previously copy-pasted in every grid */
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      flex: 1,
      minWidth: 100,
      resizable: true,
      cellRenderer: DefaultCellRenderer,
    }),
    []
  );

  /* Append the action column automatically when handlers exist */
  const columnDefs = useMemo(() => {
    const cols = [...columns];
    if (onEdit || onDelete) {
      cols.push({
        headerName: 'Actions',
        field: 'actions',
        minWidth: 170,
        maxWidth: 170,
        flex: 0,
        sortable: false,
        filter: false,
        suppressSizeToFit: true,
        cellRenderer: (params) => (
          <ActionButtons
            data={params.data}
            onEdit={onEdit}
            onDelete={onDelete}
            editLabel={editLabel}
            readOnly={isRowReadOnly ? isRowReadOnly(params.data) : false}
            readOnlyTitle={rowReadOnlyTitle}
          />
        ),
      });
    }
    return cols;
  }, [columns, onEdit, onDelete, editLabel, isRowReadOnly, rowReadOnlyTitle]);

  return (
    <div className="h-full w-full flex flex-col">
      <div
        className="ag-theme-quartz h-full w-full shadow overflow-auto border border-gray-200"
      >
        <AgGridReact
          onGridReady={handleGridReady}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowHeight={rowHeight}
          pagination={pagination}
          paginationPageSize={paginationSize}
        />
      </div>
    </div>
  );
}
